/**
 * lib/cards/template-premium.js — Premium card template.
 *
 * Chunk 2 refactor (Apr 24 2026): layout object is now built by
 * buildPremiumLayout() in build-runtime-layout.js — same function
 * the Konva preview uses. Procedural passes (banner crop, mountain
 * fallback, footer strip, footer text) stay here.
 *
 * Render order unchanged:
 *   1. White background
 *   2. Banner (image or mountain fallback)
 *   3. Footer strip + footer text
 *   4. Content elements (photo, logo, name, role, caption, QR)
 */

import { jsPDF } from 'jspdf';
import {
  CR80,
  generateQR,
  verifyUrl,
  loadImageAsDataUrl,
  deriveInitials,
  drawPhotoPlaceholder,
  contrastingTextColor,
} from './shared.js';
import { drawMountainRange } from './defaults/mountains.js';
import { renderLayoutTo } from './render-layout.js';
import {
  buildPremiumLayout,
  resolvePremiumFooterColor,
} from './build-runtime-layout.js';

/* ─── Banner image pre-crop (unchanged from Bundle C) ─── */

async function cropImageForBanner(dataUrl, sourceW, sourceH, targetWmm, targetHmm) {
  const pxPerMm = 10;
  const outW = Math.round(targetWmm * pxPerMm);
  const outH = Math.round(targetHmm * pxPerMm);

  const targetRatio = targetWmm / targetHmm;
  const sourceRatio = sourceW / sourceH;

  let sx, sy, sw, sh;
  if (sourceRatio > targetRatio) {
    sh = sourceH;
    sw = sourceH * targetRatio;
    sy = 0;
    sx = (sourceW - sw) / 2;
  } else {
    sw = sourceW;
    sh = sourceW / targetRatio;
    sx = 0;
    sy = (sourceH - sh) / 2;
  }

  const img = await loadHtmlImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

  return canvas.toDataURL('image/jpeg', 0.92);
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed during banner crop'));
    img.src = src;
  });
}

/* ─── Renderer ─── */

export async function renderPremiumCard({ staff, tenant, staffSlug }) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [CR80.widthMm, CR80.heightMm],
  });

  // ── Build layout via shared helper ──
  const { layout, derived } = buildPremiumLayout(tenant, staff);

  // ── Resolve async content ──
  const contentMap = {};
  const initials = deriveInitials(staff.first_name, staff.last_name);

  if (tenant.logo_url) {
    try {
      const { dataUrl, width, height } = await loadImageAsDataUrl(tenant.logo_url);
      contentMap.el_logo = { imageData: dataUrl, width, height, format: 'PNG' };
    } catch (e) {
      console.warn('[premium] logo load failed, text fallback:', e?.message || e);
    }
  }

  if (staff.photo_url) {
    try {
      const { dataUrl } = await loadImageAsDataUrl(staff.photo_url);
      contentMap.el_staff_photo = { imageData: dataUrl, format: 'PNG' };
    } catch (e) {
      console.warn('[premium] photo load failed, placeholder:', e?.message || e);
    }
  }

  const qrUrl = verifyUrl(tenant.slug, staffSlug);
  contentMap.el_qr = { qrDataUrl: await generateQR(qrUrl, 512) };

  contentMap.el_name = { text: derived.displayName };
  if (staff.role) contentMap.el_role = { text: staff.role };

  // ── 1. White background ──
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, CR80.widthMm, CR80.heightMm, 'F');

  // ── 2. Banner ──
  const { banner, bannerOpacity, footerStrip, brandColor } = derived;

  let bannerRendered = false;
  if (banner.imageUrl) {
    try {
      const { dataUrl, width: natW, height: natH } = await loadImageAsDataUrl(banner.imageUrl);
      const cropped = await cropImageForBanner(dataUrl, natW, natH, CR80.widthMm, banner.height);

      pdf.addImage(cropped, 'JPEG', 0, banner.topY, CR80.widthMm, banner.height, undefined, 'FAST');

      if (bannerOpacity < 1.0 && pdf.GState && pdf.setGState) {
        const whiteAlpha = 1.0 - bannerOpacity;
        pdf.setGState(new pdf.GState({ opacity: whiteAlpha }));
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, banner.topY, CR80.widthMm, banner.height, 'F');
        pdf.setGState(new pdf.GState({ opacity: 1.0 }));
      }

      bannerRendered = true;
    } catch (e) {
      console.warn('[premium] bottom banner failed, falling back to mountains:', e?.message || e);
    }
  }

  if (!bannerRendered) {
    drawMountainRange(pdf, brandColor, {
      topY: banner.mountainFallback.topY,
      showBirds: banner.mountainFallback.showBirds,
    });
  } else {
    pdf.setFillColor(brandColor.r, brandColor.g, brandColor.b);
    pdf.rect(0, footerStrip.topY, CR80.widthMm, footerStrip.height, 'F');
  }

  // ── 3. Footer text ──
  if (derived.hasFooter) {
    const auto = contrastingTextColor(brandColor);
    const color = resolvePremiumFooterColor(derived.footerMode, auto);

    pdf.setFontSize(footerStrip.fontSize);
    pdf.setFont('helvetica', footerStrip.fontWeight);
    pdf.setTextColor(color.r, color.g, color.b);
    pdf.text(derived.footerText, CR80.widthMm / 2, footerStrip.textY, { align: 'center' });
  }

  // ── 4. Content elements ──
  await renderLayoutTo(pdf, layout, contentMap, {
    brandColor,
    tenant,
    staff,
    skipBackground: true,
  });

  // ── 4a. Logo text fallback ──
  if (!contentMap.el_logo) {
    const f = derived.logoFallback;
    pdf.setFontSize(f.fontSize);
    pdf.setFont('helvetica', f.fontWeight);
    pdf.setTextColor(14, 26, 21);
    pdf.text(f.text, f.x, f.y);
  }

  // ── 4b. Photo placeholder ──
  if (!contentMap.el_staff_photo) {
    const p = derived.photoPlaceholder;
    drawPhotoPlaceholder(pdf, p.x, p.y, p.w, p.h, initials, { fontSize: p.fontSize });
  }

  return pdf;
}
